/**
 * Precificação lida da planilha de preço da empresa (D116): abas COTAÇÃO e PREÇO SINTÉTICO.
 * Lê os valores digitados e calculados; quem calcula o preço é o motor do sistema. Regras puras.
 */
import { numeroDaCelula } from "./licitacao";

export interface AbaPlanilha {
  nome: string;
  linhas: unknown[][];
}

export interface LinhaPrecoPlanilha {
  /** Linha da aba COTAÇÃO (1 = primeira linha da planilha). */
  linha: number;
  numero: number;
  /** COD da BASE DE DADOS (vira o código legado do produto). */
  cod: string | null;
  descricao: string;
  fabricante: string | null;
  fornecedor: string;
  uf: string;
  informatica: boolean | null;
  custo_prazo: number;
  desconto: number;
  st_incluso: boolean;
  url: string | null;
  /** Da aba PREÇO SINTÉTICO (nulo se a aba ou o item não existir). */
  margem: number | null;
  preco_planilha: number | null;
  erros: string[];
  /** Informações da planilha que não entram (o usuário confere). */
  avisos: string[];
}

export interface PrecificacaoPlanilha {
  linhas: LinhaPrecoPlanilha[];
  /** Problema geral (aba ausente, cabeçalho não reconhecido). */
  erro: string | null;
}

const normalizar = (v: unknown) =>
  String(v ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Célula com texto: "-" e vazio contam como vazio. */
const textoCelula = (v: unknown) => {
  const t = String(v ?? "")
    .replace(/\s+/g, " ")
    .trim();
  return t === "-" ? "" : t;
};

function acharAba(abas: AbaPlanilha[], nome: string): AbaPlanilha | undefined {
  return abas.find((a) => normalizar(a.nome) === nome) ?? abas.find((a) => normalizar(a.nome).startsWith(nome));
}

/** Linha do cabeçalho (entre as 10 primeiras) e o índice de cada coluna pelo nome normalizado. */
function colunas(linhas: unknown[][], obrigatoria: string): { cabecalho: number; indice: (nome: string) => number } | null {
  for (let i = 0; i < Math.min(10, linhas.length); i++) {
    const nomes = (linhas[i] ?? []).map(normalizar);
    if (nomes.includes(obrigatoria)) return { cabecalho: i, indice: (nome: string) => nomes.indexOf(nome) };
  }
  return null;
}

const UFS = new Set("AC AL AM AP BA CE DF ES GO MA MG MS MT PA PB PE PI PR RJ RN RO RR RS SC SE SP TO".split(" "));

function simNao(v: unknown): boolean | null {
  const t = normalizar(v);
  if (t === "s" || t === "sim") return true;
  if (t === "n" || t === "nao") return false;
  return null;
}

/** Margem e preço por item da aba PREÇO SINTÉTICO. */
function lerSintetico(aba: AbaPlanilha | undefined): Map<number, { margem: number | null; preco: number | null }> {
  const porItem = new Map<number, { margem: number | null; preco: number | null }>();
  if (!aba) return porItem;
  const c = colunas(aba.linhas, "margem");
  if (!c) return porItem;
  const [iNumero, iMargem, iPreco] = [c.indice("n edital"), c.indice("margem"), c.indice("valor unit")];
  if (iNumero < 0) return porItem;
  for (const linha of aba.linhas.slice(c.cabecalho + 1)) {
    const numero = numeroDaCelula(linha[iNumero]);
    if (numero === null || !Number.isInteger(numero)) continue;
    const margem = iMargem < 0 ? null : numeroDaCelula(linha[iMargem]);
    const preco = iPreco < 0 ? null : numeroDaCelula(linha[iPreco]);
    porItem.set(numero, {
      margem: margem === null || Number.isNaN(margem) ? null : margem,
      preco: preco === null || Number.isNaN(preco) || preco <= 0 ? null : preco,
    });
  }
  return porItem;
}

export function lerPrecificacaoPlanilha(abas: AbaPlanilha[]): PrecificacaoPlanilha {
  const cotacao = acharAba(abas, "cotacao");
  if (!cotacao) return { linhas: [], erro: "A planilha não tem a aba COTAÇÃO." };
  const c = colunas(cotacao.linhas, "local de compra");
  if (!c) return { linhas: [], erro: "A aba COTAÇÃO não tem o cabeçalho esperado (coluna LOCAL DE COMPRA)." };
  const col = {
    numero: c.indice("n"),
    cod: c.indice("cod"),
    descricao: c.indice("descricao da compra"),
    fabricante: c.indice("fabricante"),
    fornecedor: c.indice("local de compra"),
    uf: c.indice("origem"),
    informatica: c.indice("informatica s n"),
    prazo: c.indice("custo unit prazo"),
    desconto: c.indice("desconto"),
    frete: c.indice("frete"),
    custoFrete: c.indice("custo frete"),
    st: c.indice("st"),
    url: c.indice("url"),
  };
  if (col.numero < 0 || col.descricao < 0 || col.prazo < 0) {
    return { linhas: [], erro: "A aba COTAÇÃO não tem as colunas Nº, DESCRIÇÃO DA COMPRA e CUSTO UNIT.(PRAZO)." };
  }
  const celula = (linha: unknown[], i: number) => (i < 0 ? "" : linha[i]);
  const sintetico = lerSintetico(acharAba(abas, "preco sintetico"));

  const linhas: LinhaPrecoPlanilha[] = [];
  cotacao.linhas.slice(c.cabecalho + 1).forEach((linha, deslocamento) => {
    const numero = numeroDaCelula(celula(linha, col.numero));
    const descricao = textoCelula(celula(linha, col.descricao));
    const prazo = numeroDaCelula(textoCelula(celula(linha, col.prazo)));
    // Item sem compra (só a numeração do edital): não é precificação.
    if (!descricao && (prazo === null || prazo === 0)) return;
    if (numero === null || !Number.isInteger(numero) || numero <= 0) return;

    const erros: string[] = [];
    const avisos: string[] = [];
    const fornecedor = textoCelula(celula(linha, col.fornecedor));
    const uf = textoCelula(celula(linha, col.uf)).toUpperCase();
    const desconto = numeroDaCelula(textoCelula(celula(linha, col.desconto))) ?? 0;
    if (!descricao) erros.push("sem descrição da compra");
    if (prazo === null || Number.isNaN(prazo) || prazo <= 0) erros.push("sem custo a prazo");
    if (!fornecedor) erros.push("sem local de compra");
    if (!UFS.has(uf)) erros.push(uf ? `origem "${uf}" não é uma UF` : "sem origem (UF)");
    if (Number.isNaN(desconto) || desconto < 0 || desconto >= 1) erros.push("desconto fora do padrão (fração de 0 a 1)");

    const informatica = simNao(celula(linha, col.informatica));
    if (informatica === null) avisos.push("informática (S/N) não marcada");
    const st = normalizar(celula(linha, col.st));
    if (st && st !== "nao incluso" && st !== "incluso") avisos.push(`ST "${textoCelula(celula(linha, col.st))}" não reconhecido (considerado não incluso)`);
    const frete = [textoCelula(celula(linha, col.frete)), textoCelula(celula(linha, col.custoFrete))].filter(Boolean);
    if (frete.length) avisos.push(`frete da planilha (${frete.join(" / ")}) não importado: confira na precificação`);
    const urlTexto = textoCelula(celula(linha, col.url));
    const cod = textoCelula(celula(linha, col.cod));
    const dadosSintetico = sintetico.get(numero);

    linhas.push({
      linha: c.cabecalho + 2 + deslocamento,
      numero,
      cod: cod && cod !== "0" ? cod : null,
      descricao,
      fabricante: textoCelula(celula(linha, col.fabricante)) || null,
      fornecedor,
      uf,
      informatica,
      custo_prazo: prazo === null || Number.isNaN(prazo) ? 0 : prazo,
      desconto: Number.isNaN(desconto) ? 0 : desconto,
      st_incluso: st === "incluso",
      url: /^https?:\/\//i.test(urlTexto) ? urlTexto : null,
      margem: dadosSintetico?.margem ?? null,
      preco_planilha: dadosSintetico?.preco ?? null,
      erros,
      avisos,
    });
  });
  return { linhas, erro: null };
}
